import { Component, DestroyRef, EventEmitter, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ChildActivationEnd, ChildActivationStart, Router, UrlSegment } from '@angular/router';
import { Observable, Subscription, filter, first, of, switchMap } from 'rxjs';

import { ResourceService } from 'src/app/services/resource.service';
import { CountryModel } from 'src/app/shared/models/CountryModel';
import { GpAssetService } from '../gp-asset.service';

enum AssetUnderManagementTab {
  Overview = 0,
  Reports = 1,
  Documents = 2,
  Fundraisings = 3,
  Distributions = 4,
  Transactions = 5
}

interface TabInfo {
  label: string;
  // Make sure to keep it thin accessor, i.e. no complex computations, since it's being called with every change detection cycle
  labelBadge?: () => string;
  data?: {
    [key: string]: any;
  };
  route: string;
  index: AssetUnderManagementTab;
  isHidden?: boolean;
  isActive?: boolean;
}

@Component({
  selector: 'terra-asset-management-status',
  templateUrl: './asset-management-status.component.html',
  styleUrls: ['./asset-management-status.component.scss']
})
export class AssetManagementStatusComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  tabs: TabInfo[] = [
    { label: 'Overview', get labelBadge() { return this.data?.recordCount; }, route: 'overview', index: AssetUnderManagementTab.Overview },
    { label: 'Reports', route: 'reports', index: AssetUnderManagementTab.Reports },
    { label: 'Documents', route: 'documents', index: AssetUnderManagementTab.Documents },
    { label: 'Contributions', route: 'contributions', index: AssetUnderManagementTab.Fundraisings },
    { label: 'Distributions', route: 'distributions', index: AssetUnderManagementTab.Distributions },
    { label: 'Transactions', route: 'transactions', index: AssetUnderManagementTab.Transactions }
  ];

  private childTabDataSub?: Subscription;

  assetDetails$ = this.gpAssetService.holding$;

  constructor(
    private resourceService: ResourceService,
    private gpAssetService: GpAssetService,
    private route: ActivatedRoute,
    private router: Router) {
  }

  ngOnInit(): void {
    // ToDo: Check if does anything useful
    this.gpAssetService.creTransactionsTabVisible$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        const transactionsTab = this.tabs.find(t => t.index === AssetUnderManagementTab.Transactions);
        transactionsTab.isHidden = !val;
      });
    // Activates the child tab based on the route once the component loads
    this.route.firstChild.url
      .pipe(first())
      .subscribe(urls => { this.onTabChanged(urls.length ? urls[0].path : "") });

    // Reacts to navigation between child tabs
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is ChildActivationStart => event instanceof ChildActivationStart),
        switchMap(
          (): Observable<UrlSegment[]> => (this.route.firstChild && this.route.firstChild.url) || of([] as UrlSegment[]),
        ),
      )
      .subscribe(urls => { this.onTabChanging(urls.length ? urls[0].path : "") });
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event): event is ChildActivationEnd => event instanceof ChildActivationEnd),
        switchMap(
          (): Observable<UrlSegment[]> => (this.route.firstChild && this.route.firstChild.url) || of([] as UrlSegment[]),
        ),
      )
      .subscribe(urls => { this.onTabChanged(urls.length ? urls[0].path : "") });
  }

  getCountryById(countryId: number): CountryModel {
    return this.resourceService.getCountryById(countryId);
  }

  /**
   * Mark tab with requested path as inactive and clears it's cached data.
   * @param {string} oldPath - The route path of the disactivated tab.
   * @returns {void}
   */
  onTabChanging(oldPath: string): void {
    const oldTab = this.tabs.find(t => t.route == oldPath);
    if (oldTab === undefined) {
      return;
    }
    oldTab.data = null;
    oldTab.isActive = false;
  }

  /**
   * Marks tab with requested path as active.
   * @param {string} newPath - The route path of the activated tab.
   * @returns {void}
   */
  onTabChanged(newPath: string): void {
    const newTab = this.tabs.find(t => t.route == newPath);
    if (newTab === undefined) {
      return;
    }
    newTab.isActive = true;
  }

  /**
   * Called upon activation of child tab's component, subscribes to its data emitter
   * and exposes its data to the current component through the tab's data property.
   * @param {any} componentRef - The reference to the child component.
   * @returns {void}
   */
  onActivateChild(componentRef: any): void {
    const emitter: EventEmitter<any> = componentRef.exposedData;

    this.childTabDataSub?.unsubscribe();
    this.childTabDataSub = emitter?.pipe(
      takeUntilDestroyed(componentRef.destroyRef))
      .subscribe((data) => {
        const currentTab = this.tabs.find(t => t.isActive);
        if (currentTab !== undefined) {
          currentTab.data = data;
        }
      });
  }
}
